import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Stack,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  CasinoOutlined as RevolverIcon,
  SportsEsportsOutlined as MineIcon,
  AutoAwesomeOutlined as MRLIcon,
  RocketLaunchOutlined as MissileIcon,
  AppsOutlined as AllIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx, fontFamilies, customShadows, LOTTERY_NAMES, LOTTERY_TYPE_IDS } from "../../../../../theme";
import {
  CommonInput,
  CommonSelect,
  CommonButton,
} from "../../../../Components/ui";

const RESULTS_SORT_OPTIONS = [
  { value: "newest", label: "Newest Draw First" },
  { value: "oldest", label: "Oldest Draw First" },
  { value: "highestPrize", label: "Highest Prize Won" },
  { value: "lowestPrize", label: "Lowest Prize Won" },
];

const GAME_TABS = [
  { id: "all", label: "All Games", icon: AllIcon, color: colors.primary.main },
  { id: String(LOTTERY_TYPE_IDS.SPINBET), label: LOTTERY_NAMES.SPINBET, icon: RevolverIcon, color: colors.primary.main },
  { id: String(LOTTERY_TYPE_IDS.MINEBET), label: LOTTERY_NAMES.MINEBET, icon: MineIcon, color: colors.purple[600] },
  { id: String(LOTTERY_TYPE_IDS.MEGABET), label: LOTTERY_NAMES.MEGABET, icon: MRLIcon, color: colors.emerald[600] },
  { id: String(LOTTERY_TYPE_IDS.ROCKETBET), label: LOTTERY_NAMES.ROCKETBET, icon: MissileIcon, color: colors.amber[600] },
];

const ResultsFilters = ({
  activeTab = "all",
  onTabChange,
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  countsByType = {},
  onRefresh,
  loading = false,
}) => {
  return (
    <Box
      sx={{
        bgcolor: colors.dashboard.cardBg,
        borderRadius: borderRadiusPx.xl,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        p: { xs: 2, md: 2.5 },
        mb: 4,
        boxShadow: customShadows.cardFlat,
      }}
    >
      {/* Category Tabs */}
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{
          mb: 2.5,
          overflowX: "auto",
          pb: { xs: 1, sm: 0 },
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {GAME_TABS.map((tab) => {
          const isSelected = String(activeTab) === String(tab.id);
          const count = tab.id === "all" ? countsByType.all ?? 0 : countsByType[tab.id] ?? 0;
          const TabIcon = tab.icon;

          return (
            <Box
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.9,
                borderRadius: borderRadiusPx.lg,
                cursor: "pointer",
                userSelect: "none",
                whiteSpace: "nowrap",
                transition: "all 0.2s ease-in-out",
                backgroundColor: isSelected ? colors.primary.main : colors.slate[50],
                border: `1px solid ${isSelected ? colors.primary.main : colors.slate[200]}`,
                boxShadow: isSelected ? "0 4px 12px rgba(37, 99, 235, 0.25)" : "none",
                "&:hover": {
                  backgroundColor: isSelected ? colors.primary.dark : colors.slate[100],
                  borderColor: isSelected ? colors.primary.dark : colors.slate[300],
                },
              }}
            >
              <TabIcon sx={{ fontSize: 18, color: isSelected ? colors.neutral.white : tab.color }} />
              <Typography
                sx={{
                  fontFamily: fontFamilies.primary,
                  fontWeight: isSelected ? 700 : 600,
                  fontSize: "0.875rem",
                  color: isSelected ? colors.neutral.white : colors.slate[700],
                }}
              >
                {tab.label}
              </Typography>
              <Box
                sx={{
                  px: 0.85,
                  py: 0.15,
                  borderRadius: borderRadiusPx.pill,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  fontFamily: fontFamilies.primary,
                  backgroundColor: isSelected ? "rgba(255, 255, 255, 0.25)" : colors.slate[200],
                  color: isSelected ? colors.neutral.white : colors.slate[700],
                }}
              >
                {count}
              </Box>
            </Box>
          );
        })}
      </Stack>

      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        {/* Search Field */}
        <Grid item xs={12} sm={6} md={5}>
          <CommonInput
            placeholder="Search by Lottery #ID, Winner (0x...), or Creator..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.slate[400], fontSize: 20 }} />
              </InputAdornment>
            }
            endAdornment={
              searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => onSearchChange("")}
                    edge="end"
                    sx={{ color: colors.slate[400] }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
            fullWidth
            size="small"
          />
        </Grid>

        {/* Sorting & Refresh */}
        <Grid item xs={12} sm={6} md={5}>
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            alignItems="center"
          >
            <Box sx={{ minWidth: 190, flexGrow: { xs: 1, sm: 0 } }}>
              <CommonSelect
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                options={RESULTS_SORT_OPTIONS}
                size="small"
              />
            </Box>
            <CommonButton
              variant="outline"
              size="small"
              onClick={onRefresh}
              disabled={loading}
              sx={{ p: 1, minWidth: 40 }}
            >
              <RefreshIcon
                sx={{
                  fontSize: 18,
                  animation: loading ? "spin 1s linear infinite" : undefined,
                }}
              />
            </CommonButton>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

ResultsFilters.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  countsByType: PropTypes.object,
  onRefresh: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default ResultsFilters;
